import { useEffect, useState } from "react";
import { CreditCard, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { paymentMethodsApi } from "../lib/api";

const emptyForm = {
  id: "",
  code: "",
  name: "",
  provider: "pakasir",
  method_type: "qris",
  description: "",
  is_active: true,
  sort_order: 0,
};

const AdminPaymentMethodsPage = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadMethods = async () => {
    try {
      setLoading(true);
      const data = await paymentMethodsApi.getAll();
      setMethods(data || []);
    } catch (error) {
      toast.error(error.message || "Gagal memuat metode bayar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMethods();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (method) => {
    setForm({ ...emptyForm, ...method, description: method.description || "" });
    setDialogOpen(true);
  };

  const saveMethod = async (event) => {
    event.preventDefault();
    if (!form.code.trim()) return toast.error("Kode metode wajib diisi");
    if (!form.name.trim()) return toast.error("Nama metode wajib diisi");

    const payload = {
      code: form.code,
      name: form.name,
      provider: form.provider,
      method_type: form.method_type,
      description: form.description || null,
      is_active: form.is_active,
      sort_order: Number(form.sort_order || 0),
    };

    try {
      setSaving(true);
      if (form.id) {
        await paymentMethodsApi.update(form.id, payload);
        toast.success("Metode bayar diperbarui");
      } else {
        await paymentMethodsApi.create(payload);
        toast.success("Metode bayar ditambahkan");
      }
      setDialogOpen(false);
      await loadMethods();
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan metode bayar");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (method) => {
    try {
      await paymentMethodsApi.updateStatus(method.id, !method.is_active);
      await loadMethods();
    } catch (error) {
      toast.error(error.message || "Gagal mengubah status");
    }
  };

  const deleteMethod = async (method) => {
    if (!window.confirm(`Hapus metode bayar ${method.name}?`)) return;
    try {
      await paymentMethodsApi.delete(method.id);
      toast.success("Metode bayar dihapus");
      await loadMethods();
    } catch (error) {
      toast.error(error.message || "Gagal menghapus metode bayar");
    }
  };

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
              Payment Admin
            </p>
            <h1 className="mt-1 text-3xl font-semibold text-stone-950">
              Metode Pembayaran
            </h1>
            <p className="mt-2 max-w-3xl text-stone-600">
              Aktifkan atau nonaktifkan channel Pakasir seperti QRIS dan Virtual Account untuk checkout publik.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={loadMethods} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button onClick={openNew} className="bg-emerald-600 text-white hover:bg-emerald-700">
              <Plus className="mr-2 h-4 w-4" /> Tambah Metode
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-700" /> Daftar Metode Bayar
            </CardTitle>
          </CardHeader>
        <CardContent>
            <div className="hidden overflow-x-auto rounded-lg border border-stone-200 md:block">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-stone-50 text-stone-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Kode</th>
                    <th className="px-4 py-3 font-medium">Nama</th>
                    <th className="px-4 py-3 font-medium">Provider</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white">
                  {loading ? (
                    <tr><td className="px-4 py-10 text-center text-stone-500" colSpan={5}>Memuat metode bayar...</td></tr>
                  ) : methods.length === 0 ? (
                    <tr><td className="px-4 py-10 text-center text-stone-500" colSpan={5}>Belum ada metode bayar.</td></tr>
                  ) : (
                    methods.map((method) => (
                      <tr key={method.id}>
                        <td className="px-4 py-3 font-mono text-stone-900">{method.code}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-stone-900">{method.name}</p>
                          {method.description && <p className="text-xs text-stone-500">{method.description}</p>}
                        </td>
                        <td className="px-4 py-3 text-stone-700">{method.provider} / {method.method_type}</td>
                        <td className="px-4 py-3">
                          <Switch checked={method.is_active} onCheckedChange={() => toggleStatus(method)} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openEdit(method)}>
                              <Pencil className="mr-1 h-4 w-4" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => deleteMethod(method)}>
                              <Trash2 className="mr-1 h-4 w-4" /> Hapus
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 md:hidden">
              {loading ? (
                <div className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-stone-500">
                  Memuat metode bayar...
                </div>
              ) : methods.length === 0 ? (
                <div className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-stone-500">
                  Belum ada metode bayar.
                </div>
              ) : (
                methods.map((method) => (
                  <div key={method.id} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-mono text-xs text-stone-500">{method.code}</p>
                        <p className="font-semibold text-stone-900">{method.name}</p>
                        {method.description && <p className="mt-1 text-sm text-stone-500">{method.description}</p>}
                        <p className="mt-2 text-sm text-stone-600">{method.provider} / {method.method_type}</p>
                      </div>
                      <Switch checked={method.is_active} onCheckedChange={() => toggleStatus(method)} />
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(method)}>
                        <Pencil className="mr-1 h-4 w-4" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => deleteMethod(method)}>
                        <Trash2 className="mr-1 h-4 w-4" /> Hapus
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{form.id ? "Edit Metode Bayar" : "Tambah Metode Bayar"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveMethod} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Kode Pakasir</Label>
                  <Input value={form.code} onChange={(e) => setForm((current) => ({ ...current, code: e.target.value }))} placeholder="qris" />
                </div>
                <div>
                  <Label>Nama</Label>
                  <Input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} placeholder="QRIS" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Provider</Label>
                  <Select value={form.provider} onValueChange={(value) => setForm((current) => ({ ...current, provider: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pakasir">Pakasir</SelectItem>
                      <SelectItem value="cod">COD</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipe</Label>
                  <Select value={form.method_type} onValueChange={(value) => setForm((current) => ({ ...current, method_type: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qris">QRIS</SelectItem>
                      <SelectItem value="va">Virtual Account</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="cod">COD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Deskripsi</Label>
                <Input value={form.description} onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Urutan</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm((current) => ({ ...current, sort_order: e.target.value }))} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50 p-4">
                  <Label>Aktif</Label>
                  <Switch checked={form.is_active} onCheckedChange={(value) => setForm((current) => ({ ...current, is_active: value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-700">Simpan</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </AdminLayout>
  );
};

export default AdminPaymentMethodsPage;

